///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The scrubber: edge labels, the track with past/future zones, midnight
/// ticks, the now marker and a draggable playhead, the per-layer data
/// lanes under it, and the scrub bubble that names the time (and which
/// layers have data there) under the pointer. Pure view over the model.

import { formatEdgeTime, formatTickTime } from "@/utils/timeControl"
import { cn } from "@/utils/utils"
import { ChevronLeftIcon, ChevronRightIcon, LockIcon } from "lucide-react"
import { useState } from "react"
import type { TimeControlModel } from "./timeControlModel"

// Diagonal hatch for the free tier's locked-history cap
const cHatch = "repeating-linear-gradient(135deg, rgba(255,255,255,0.28) 0 2px, transparent 2px 6px)"

// Lock cap width; the lane rows indent by the same amount to stay aligned
// with the track
const cLockCapClass = "w-5"

export function TimeStrip({ model }: { model: TimeControlModel }) {
	const { actions, lanes, timeZoneMode, isExpanded, isLive } = model
	const isFree = !model.window.isPaid

	// Pointer position within the track (0-1): hover on desktop, the
	// finger while dragging on touch; drives the scrub bubble
	const [pointerPct, setPointerPct] = useState<number | null>(null)

	const fractionAt = (el: HTMLElement, clientX: number) => {
		const rect = el.getBoundingClientRect()
		return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
	}

	// Pointer capture keeps move/up events flowing to the track during a drag
	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		e.preventDefault()
		try {
			e.currentTarget.setPointerCapture(e.pointerId)
		} catch {
			// capture is best-effort: a tap still scrubs without it
		}
		const fraction = fractionAt(e.currentTarget, e.clientX)
		setPointerPct(fraction)
		actions.scrubToFraction(fraction)
	}

	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const fraction = fractionAt(e.currentTarget, e.clientX)
		setPointerPct(fraction)
		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			actions.scrubToFraction(fraction)
		}
	}

	// Fingers don't hover: drop the bubble when the drag ends
	const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.pointerType !== "mouse") setPointerPct(null)
	}

	const pointerMs = pointerPct !== null ? model.startMs + model.spanMs * pointerPct : null

	const startLabel = formatEdgeTime(model.startMs, model.nowMs, timeZoneMode)
	const endLabel = formatEdgeTime(model.endMs, model.nowMs, timeZoneMode)
	const labelClass = "text-[10px] font-mono tabular-nums text-white/55 leading-none whitespace-nowrap"

	return (
		<div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-1.5 px-2 pt-1">
			<span className={labelClass} aria-hidden="true">{startLabel}</span>

			{/* Track row. Free users get a hatched, locked cap at the history end
			    (the pricing doc's "locked region with a CTA") that opens the
			    subscribe flow; the track itself keeps its true scale. The
			    horizontal padding (shared by the lane rows) gives the knob
			    room to overhang the track ends without touching the labels */}
			<div className="flex items-center gap-1 min-w-0 px-1.5">
				{isFree && (
					<button
						type="button"
						onClick={actions.openUpsell}
						className={cn(
							cLockCapClass,
							"shrink-0 h-3 flex items-center justify-center rounded-l-full border border-white/15 bg-cyan-500/10 hover:bg-cyan-500/25 transition-colors"
						)}
						style={{ backgroundImage: cHatch }}
						title="Scrub up to 48 hours of history with a subscription — click to upgrade"
						aria-label="Unlock 48-hour history"
					>
						<LockIcon className="w-2 h-2 text-cyan-200" />
					</button>
				)}

				{/* Hit area is taller than the visible track for touch */}
				<div
					className="relative flex-1 py-1.5 cursor-pointer touch-none"
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onPointerLeave={() => setPointerPct(null)}
					role="slider"
					aria-label="Scrub time"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={Math.round(model.playheadPct)}
					aria-valuetext={formatTickTime(model.currentMs, timeZoneMode)}
					title="Drag to scrub time"
				>
					{pointerMs !== null && <ScrubBubble model={model} ms={pointerMs} pct={pointerPct! * 100} />}

					<div
						className={cn("relative h-1.5 rounded-full overflow-hidden", isFree && "rounded-l-none")}
						style={{
							// faint past (blue) / future (amber) zones split at now, matching the steppers
							background: `linear-gradient(to right, rgba(96,165,250,0.18) 0%, rgba(96,165,250,0.18) ${model.nowPct}%, rgba(251,191,36,0.18) ${model.nowPct}%, rgba(251,191,36,0.18) 100%)`
						}}
					>
						{/* Played region: neutral, so color stays reserved for now/live */}
						<div className="absolute top-0 bottom-0 left-0 bg-white/35" style={{ width: `${model.playheadPct}%` }} />
						{model.midnights.map(m => (
							<div key={m.ms} className="absolute top-0 bottom-0 w-px bg-white/50" style={{ left: `${m.pct}%` }} />
						))}
					</div>

					{/* "Now" marker: green is reserved for now/live. While live the
					    playhead sits on it, so the marker grows into the knob */}
					<div
						className={cn(
							"absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-green-400 pointer-events-none transition-all duration-200",
							isLive ? "w-3 h-3 shadow ring-2 ring-black/40" : "w-0.5 h-3"
						)}
						style={{ left: `${model.nowPct}%` }}
					/>

					{/* Playhead knob: the drag affordance. Hidden while live (the
					    now marker is the playhead) and while out of the window */}
					{!isLive && !model.beforeWindow && !model.afterWindow && (
						<div
							className={cn(
								"absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full shadow ring-2 ring-black/40 pointer-events-none",
								model.isAnimating ? "bg-orange-200" : "bg-white"
							)}
							style={{ left: `${model.playheadPct}%` }}
						/>
					)}

					{/* Playhead left the window: chevrons bring it back to the edge */}
					{model.beforeWindow && (
						<EdgeJump side="start" model={model} />
					)}
					{model.afterWindow && (
						<EdgeJump side="end" model={model} />
					)}
				</div>
			</div>

			<span className={labelClass} aria-hidden="true">{endLabel}</span>

			{/* Dataset availability lanes: one thin bar per visible time-based
			    dataset spanning its data range; bright segments are frames
			    loaded and ready to show. Always on, since they answer the
			    question the strip exists for: what is there to see, when */}
			{lanes.length > 0 && (
				<div className={cn("col-start-2 flex gap-1 px-1.5", isExpanded ? "mt-0.5" : "-mt-0.5")}>
					{isFree && <div className={cn(cLockCapClass, "shrink-0")} />}
					<div className={cn("flex-1 flex flex-col", isExpanded ? "gap-[2px]" : "gap-px")}>
						{lanes.map(lane => {
							if (lane.endPct - lane.startPct < 0.5) return null
							return (
								<div
									key={lane.id}
									className={cn("relative", isExpanded ? "h-[3px]" : "h-0.5")}
									title={lane.waiting
										? `${lane.label}: loading the frame for the current time`
										: `${lane.label}: available (dim) and loaded (bright)`}
								>
									<div
										className="absolute top-0 bottom-0 rounded-full overflow-hidden"
										style={{ left: `${lane.startPct}%`, width: `${lane.endPct - lane.startPct}%`, background: lane.color, opacity: 0.3 }}
									>
										{/* Buffering shimmer over the unloaded span while this
										    lane waits on the clock's frame (video idiom) */}
										{lane.waiting && <div className="eb-lane-shimmer absolute inset-y-0 w-1/3" />}
									</div>
									{lane.loaded.map(seg => (
										<div
											key={seg.startPct}
											className="absolute top-0 bottom-0 rounded-full"
											style={{ left: `${seg.startPct}%`, width: `${seg.endPct - seg.startPct}%`, background: lane.color, opacity: 0.95 }}
										/>
									))}
								</div>
							)
						})}
					</div>
				</div>
			)}

			{/* Day ruler under multi-day windows (expanded): names the midnight ticks */}
			{isExpanded && model.midnights.length > 0 && (
				<div className="col-start-2 flex gap-1 px-1.5 mt-0.5">
					{isFree && <div className={cn(cLockCapClass, "shrink-0")} />}
					<div className="relative flex-1 h-3 text-[9px] font-mono text-white/45 leading-3">
						{model.midnights.map(m => {
							if (m.pct < 6 || m.pct > 94) return null
							return (
								<span key={m.ms} className="absolute -translate-x-1/2" style={{ left: `${m.pct}%` }}>
									{m.label}
								</span>
							)
						})}
					</div>
				</div>
			)}

			{/* Lane legend (expanded) */}
			{isExpanded && lanes.length > 0 && (
				<div className="col-span-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-white/60 leading-3">
					{lanes.map(lane => (
						<span key={lane.id} className="flex items-center gap-1">
							<span className={cn("w-1.5 h-1.5 rounded-full", lane.waiting && "eb-lane-pulse")} style={{ background: lane.color }} />
							{lane.label}
						</span>
					))}
					<span className="text-white/35">bright = loaded</span>
				</div>
			)}
		</div>
	)
}

/** Time under the pointer, plus which datasets have data there */
function ScrubBubble({ model, ms, pct }: { model: TimeControlModel; ms: number; pct: number }) {
	return (
		<div
			className="absolute bottom-full mb-1 -translate-x-1/2 pointer-events-none whitespace-nowrap px-1.5 py-1 rounded-md bg-black/80 border border-white/20 text-[10px] font-mono text-white/90 shadow-lg z-10"
			style={{ left: `${pct}%` }}
		>
			{formatTickTime(ms, model.timeZoneMode)}
			{/* Three states per lane: loaded (solid dot), available but not
			    loaded yet (ringed dot, pulsing: data will come), outside the
			    data (hollow, still: it won't) */}
			{model.lanes.map(lane => {
				const available = ms >= lane.startMs && ms <= lane.endMs
				const loaded = lane.loadedRanges.some(r => ms >= r.startMs && ms <= r.endMs)
				return (
					<div key={lane.id} className={cn("flex items-center gap-1 font-sans leading-4", available ? "text-white/90" : "text-white/40")}>
						<span
							className={cn("w-1.5 h-1.5 rounded-full border", available && !loaded && "eb-lane-pulse")}
							style={{
								background: loaded ? lane.color : "transparent",
								borderColor: available ? lane.color : "rgba(255,255,255,0.4)"
							}}
						/>
						{lane.label}
						{available && !loaded && <span className="text-cyan-300/80">loading</span>}
						{!available && <span className="text-white/30">no data</span>}
					</div>
				)
			})}
		</div>
	)
}

/** Amber chevron at a track edge that brings an out-of-window clock back */
function EdgeJump({ side, model }: { side: "start" | "end"; model: TimeControlModel }) {
	const isStart = side === "start"
	const targetMs = isStart ? model.startMs : model.endMs
	return (
		<button
			type="button"
			onPointerDown={e => e.stopPropagation()}
			onClick={() => model.actions.jumpTo(targetMs)}
			className={cn(
				"absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-amber-400 text-black flex items-center justify-center shadow",
				isStart ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2"
			)}
			title={`Clock is ${isStart ? "before" : "after"} the window (${formatTickTime(model.currentMs, model.timeZoneMode)}) — click to jump to the window ${side}`}
			aria-label={`Jump to window ${side}`}
		>
			{isStart ? <ChevronLeftIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
		</button>
	)
}
